/**
 * CEP Command shell — left CRM rail + top navbar.
 * All member pages should render through this (or VIPLayout, which wraps it).
 */
import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "wouter";
import { useAuth } from "@/_core/hooks/useAuth";
import { useVIPAccess } from "@/hooks/useVIPAccess";
import {
  LayoutDashboard,
  GitBranch,
  Users,
  Activity,
  ListFilter,
  Flame,
  AlertTriangle,
  Moon,
  ArrowUpRight,
  Vault,
  Wrench,
  Mail,
  Calendar,
  Layers,
  Plug,
  Settings,
  BarChart3,
  Crosshair,
  Target,
  Magnet,
  Package,
  BookOpen,
  Calculator,
  Menu,
  X,
  LogOut,
  Loader2,
  ChevronDown,
  ChevronRight,
  FileSpreadsheet,
} from "lucide-react";
import { vipDisplayName, VIP } from "@/lib/vipTheme";
import { VipLogoMark } from "@/components/VipIcon";
import "@/styles/vip-tactical.css";

type NavLeaf = { label: string; path: string; icon?: React.ReactNode; external?: boolean };

const SMART_LISTS: NavLeaf[] = [
  { label: "Hot leads", path: "/command/smart-lists/hot", icon: <Flame size={14} /> },
  { label: "Churn risk", path: "/command/smart-lists/churn", icon: <AlertTriangle size={14} /> },
  { label: "Inactive", path: "/command/smart-lists/inactive", icon: <Moon size={14} /> },
  { label: "Ready to move", path: "/command/smart-lists/upgrade", icon: <ArrowUpRight size={14} /> },
  { label: "Top prospects", path: "/command/smart-lists/vault", icon: <Vault size={14} /> },
];

const TOOLS: NavLeaf[] = [
  { label: "Automation", path: "/command/tools/automation", icon: <Wrench size={14} /> },
  { label: "Drip sequences", path: "/command/tools/drip", icon: <Layers size={14} /> },
  { label: "Reports", path: "/command/tools/reports", icon: <BarChart3 size={14} /> },
  { label: "Integrations", path: "/command/tools/integrations", icon: <Plug size={14} /> },
  { label: "Gmail", path: "/command/tools/gmail", icon: <Mail size={14} /> },
  { label: "Calendar", path: "/command/tools/calendar", icon: <Calendar size={14} /> },
  { label: "Settings", path: "/command/tools/settings", icon: <Settings size={14} /> },
];

/** Left rail primary — full operator map */
const PRIMARY: NavLeaf[] = [
  { label: "Command", path: "/command/dashboard", icon: <LayoutDashboard size={16} /> },
  { label: "Deal Tracker", path: "/command/deals", icon: <FileSpreadsheet size={16} /> },
  { label: "Pipeline", path: "/command/pipeline", icon: <GitBranch size={16} /> },
  { label: "Duplicates", path: "/command/duplicates", icon: <AlertTriangle size={16} /> },
  { label: "Contacts", path: "/command/contacts", icon: <Users size={16} /> },
  { label: "Activities", path: "/command/activity", icon: <Activity size={16} /> },
  { label: "Deal Engine", path: "/command/deal-engine", icon: <Calculator size={16} /> },
  { label: "Comp / MAO", path: "/command/comp", icon: <Target size={16} /> },
  { label: "Intelligence", path: "/command/intelligence", icon: <BarChart3 size={16} /> },
];

const OPS: NavLeaf[] = [
  { label: "OP 1 — Sniper", path: "/command/op1", icon: <Crosshair size={14} /> },
  { label: "OP 2 — General", path: "/command/op2", icon: <Target size={14} /> },
  { label: "OP 3 — Magnet", path: "/command/op3", icon: <Magnet size={14} /> },
];

const SYSTEMS: NavLeaf[] = [
  { label: "Gadgets", path: "/command/gadgets", icon: <Calculator size={14} /> },
  { label: "Training", path: "/command/training", icon: <BookOpen size={14} /> },
  { label: "Resources", path: "/command/resources", icon: <Package size={14} /> },
  { label: "Account", path: "/command/account", icon: <Settings size={14} /> },
];

/**
 * Top ticket pills — equal size, original chrome language.
 * High-frequency destinations only; rest live in dropdowns + left rail.
 */
const TOP_PILLS: Array<{ label: string; path: string; external?: boolean }> = [
  { label: "COMMAND", path: "/command/dashboard" },
  { label: "DEAL TRACKER", path: "/command/deals" },
  { label: "PIPELINE", path: "/command/pipeline" },
  { label: "GADGETS", path: "/command/gadgets" },
  { label: "TRAINING", path: "/command/training" },
  { label: "ACCOUNT", path: "/command/account" },
];

const TOP_MORE: Array<{ label: string; path: string; external?: boolean }> = [
  { label: "Deal Engine", path: "/command/deal-engine" },
  { label: "Comp / MAO", path: "/command/comp" },
  { label: "Intelligence", path: "/command/intelligence" },
  { label: "Contacts", path: "/command/contacts" },
  { label: "Activities", path: "/command/activity" },
  { label: "Duplicates", path: "/command/duplicates" },
  { label: "Resources", path: "/command/resources" },
];

const TOP_OPS = [
  { label: "OP1", path: "/command/op1", title: "OP 1 — Solo Sniper" },
  { label: "OP2", path: "/command/op2", title: "OP 2 — General" },
  { label: "OP3", path: "/command/op3", title: "OP 3 — Magnet" },
];

function pathActive(location: string, path: string) {
  if (path === "/command/dashboard") {
    return location === "/command" || location === "/command/dashboard" || location === "/command/command";
  }
  if (path === "/command/deals") {
    return location === "/command/deals" || location.startsWith("/command/deal-tracker");
  }
  if (location === path || location.startsWith(path + "/")) return true;
  return false;
}

interface EliteOperatorLayoutProps {
  children: React.ReactNode;
  title?: string;
}

export default function EliteOperatorLayout({ children, title }: EliteOperatorLayoutProps) {
  const [location] = useLocation();
  const [railOpen, setRailOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({
    lists: true,
    tools: false,
    ops: true,
  });
  const [openDrop, setOpenDrop] = useState<"more" | "ops" | null>(null);
  const dropRef = useRef<HTMLDivElement>(null);
  const { user, logout } = useAuth();
  const { isVIP, loading: vipLoading } = useVIPAccess();
  const isAdmin = (user as { role?: string } | null)?.role === "admin";
  const displayName = vipDisplayName(user as Parameters<typeof vipDisplayName>[0]);
  const operatorMode = (user as { operatorMode?: string | null } | null)?.operatorMode;

  const visibleOps = TOP_OPS.filter(
    (op) => isAdmin || operatorMode === op.label.toLowerCase() || !operatorMode
  );
  // Always show all three for admin; for scoped mode show all so the owner can still navigate when reviewing
  const opPills = isAdmin || !operatorMode ? TOP_OPS : visibleOps.length ? visibleOps : TOP_OPS;

  const toggle = (id: string) => setOpenGroups((g) => ({ ...g, [id]: !g[id] }));

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!dropRef.current?.contains(e.target as Node)) setOpenDrop(null);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  if (vipLoading) {
    return (
      <div className="vip-tactical elite-shell" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Loader2 size={32} color={VIP.titleBlue} style={{ animation: "spin 1s linear infinite" }} />
      </div>
    );
  }

  if (!isVIP && !isAdmin) return null;

  const renderLeaf = (item: NavLeaf, onNav?: () => void) => {
    if (item.external) {
      return (
        <a
          key={item.path}
          href={item.path}
          target="_blank"
          rel="noopener noreferrer"
          className="elite-nav-item"
          onClick={onNav}
        >
          {item.icon}
          <span>{item.label}</span>
        </a>
      );
    }
    const active = pathActive(location, item.path);
    return (
      <Link key={item.path} href={item.path}>
        <button type="button" className={`elite-nav-item${active ? " is-active" : ""}`} onClick={onNav}>
          {item.icon}
          <span>{item.label}</span>
        </button>
      </Link>
    );
  };

  const rail = (
    <>
      <div className="elite-rail-brand">
        <Link href="/command/dashboard">
          <div className="elite-rail-logo" title="CEP Command">
            <VipLogoMark height={42} />
          </div>
        </Link>
        <div>
          <div className="elite-rail-title">CEP COMMAND</div>
          <div className="elite-rail-sub">CALIFORNIA EQUITY PROGRAM</div>
        </div>
      </div>

      <nav className="elite-rail-nav">
        <div className="elite-nav-section">
          {PRIMARY.map((item) => renderLeaf(item, () => setRailOpen(false)))}
        </div>

        <div className="elite-nav-section">
          <button type="button" className="elite-nav-group-h" onClick={() => toggle("lists")}>
            <ListFilter size={14} />
            <span>Smart Lists</span>
            {openGroups.lists ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>
          {openGroups.lists && (
            <div className="elite-nav-sub">{SMART_LISTS.map((i) => renderLeaf(i, () => setRailOpen(false)))}</div>
          )}
        </div>

        <div className="elite-nav-section">
          <button type="button" className="elite-nav-group-h" onClick={() => toggle("ops")}>
            <Crosshair size={14} />
            <span>Operator Modes</span>
            {openGroups.ops ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>
          {openGroups.ops && (
            <div className="elite-nav-sub">{OPS.map((i) => renderLeaf(i, () => setRailOpen(false)))}</div>
          )}
        </div>

        <div className="elite-nav-section">
          <button type="button" className="elite-nav-group-h" onClick={() => toggle("tools")}>
            <Wrench size={14} />
            <span>Tools</span>
            {openGroups.tools ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>
          {openGroups.tools && (
            <div className="elite-nav-sub">{TOOLS.map((i) => renderLeaf(i, () => setRailOpen(false)))}</div>
          )}
        </div>

        <div className="elite-nav-section">
          <div className="elite-nav-group-label">Systems</div>
          {SYSTEMS.map((i) => renderLeaf(i, () => setRailOpen(false)))}
        </div>
      </nav>

      <div className="elite-rail-foot">
        <div className="elite-rail-user" title={displayName}>
          <span className="elite-rail-user-name">
            {displayName}
            {isAdmin ? " · ADMIN" : ""}
          </span>
          <button type="button" className="elite-rail-logout" onClick={() => logout()} title="Sign out">
            <LogOut size={14} />
          </button>
        </div>
      </div>
    </>
  );

  return (
    <div className="vip-tactical elite-shell">
      <aside className="elite-rail elite-rail-desktop">{rail}</aside>

      {railOpen && (
        <div className="elite-rail-overlay" role="dialog" aria-label="Navigation">
          <aside className="elite-rail elite-rail-mobile">
            <button type="button" className="elite-rail-close" onClick={() => setRailOpen(false)} aria-label="Close menu">
              <X size={22} />
            </button>
            {rail}
          </aside>
          <button type="button" className="elite-rail-scrim" onClick={() => setRailOpen(false)} aria-label="Close" />
        </div>
      )}

      <div className="elite-main">
        {/* Top ticket navbar — original chrome, equal pills + dropdowns */}
        <header className="elite-topnav" ref={dropRef}>
          <button type="button" className="elite-hamburger" onClick={() => setRailOpen(true)} aria-label="Open menu">
            <Menu size={22} />
          </button>

          <div className="elite-topnav-brand elite-topnav-brand-mobile">
            <VipLogoMark height={36} />
          </div>

          <nav className="elite-topnav-pills" aria-label="Primary">
            {TOP_PILLS.map((item) => {
              const active = pathActive(location, item.path);
              return (
                <Link key={item.path} href={item.path}>
                  <button
                    type="button"
                    className={`vip-pill is-ghost elite-top-pill${active ? " is-active" : ""}`}
                    title={item.label}
                  >
                    {item.label}
                  </button>
                </Link>
              );
            })}

            {/* MORE dropdown */}
            <div className="elite-top-drop">
              <button
                type="button"
                className={`vip-pill is-ghost elite-top-pill${openDrop === "more" ? " is-active" : ""}`}
                onClick={() => setOpenDrop((d) => (d === "more" ? null : "more"))}
              >
                MORE <ChevronDown size={12} />
              </button>
              {openDrop === "more" && (
                <div className="elite-top-menu">
                  {TOP_MORE.map((item) =>
                    item.external ? (
                      <a
                        key={item.path}
                        href={item.path}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="elite-top-menu-item"
                        onClick={() => setOpenDrop(null)}
                      >
                        {item.label}
                      </a>
                    ) : (
                      <Link key={item.path} href={item.path}>
                        <button
                          type="button"
                          className={`elite-top-menu-item${pathActive(location, item.path) ? " is-active" : ""}`}
                          onClick={() => setOpenDrop(null)}
                        >
                          {item.label}
                        </button>
                      </Link>
                    )
                  )}
                </div>
              )}
            </div>
          </nav>

          <div className="elite-topnav-ops" aria-label="Operator modes">
            {opPills.map((op) => (
              <Link key={op.path} href={op.path}>
                <button
                  type="button"
                  title={op.title}
                  className={`vip-op-btn${pathActive(location, op.path) ? " is-active" : ""}`}
                >
                  {op.label}
                </button>
              </Link>
            ))}
          </div>

          {title && <div className="elite-topnav-page-title">{title}</div>}
        </header>

        <div className="elite-content">{children}</div>
      </div>

    </div>
  );
}
